/**
 * Interaction Ledger
 * 
 * Tracks all user interactions and app state changes for debugging.
 * Accessible via window.interactionLedger in the console.
 */

class InteractionLedger {
  constructor() {
    this.logs = [];
    this.maxLogs = 1000; // Keep last 1000 entries
    this.isEnabled = true;
    this.filters = {
      userInteractions: true,
      stateChanges: true,
      urlChanges: true,
      errors: true,
    };
  }

  /**
   * Add a log entry
   * @param {string} type - Type of interaction (click, stateChange, urlChange, etc.)
   * @param {string} category - Category (userInteraction, appState, urlSync, etc.)
   * @param {Object} data - Additional data about the interaction
   * @param {string} description - Human-readable description
   */
  log(type, category, data = {}, description = '') {
    if (!this.isEnabled) return;

    const entry = {
      timestamp: new Date().toISOString(),
      time: Date.now(),
      type,
      category,
      data: this.sanitizeData(data),
      description,
      stackTrace: type >= 50 ? this.getStackTrace() : null,
    };

    this.logs.push(entry);

    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Also log to console in development
    if (process.env.NODE_ENV === 'development' || !process.env.NODE_ENV) {
      console.log(`[Ledger] ${type}: ${description}`, entry);
    }
  }

  /**
   * Sanitize data to prevent circular references
   */
  sanitizeData(data) {
    try {
      // Remove functions and circular references
      const seen = new WeakSet();
      return JSON.parse(JSON.stringify(data, (key, value) => {
        if (typeof value === 'function') {
          return '[Function]';
        }
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return '[Circular]';
          }
          seen.add(value);
        }
        return value;
      }));
    } catch (error) {
      return { error: 'Failed to serialize data', message: error.message };
    }
  }

  /**
   * Get stack trace (excluding this file)
   */
  getStackTrace() {
    try {
      const stack = new Error().stack;
      if (!stack) return null;
      
      // Filter out this file and internal calls
      return stack
        .split('\n')
        .slice(3, 8) // Get 5 frames
        .map(line => line.trim())
        .filter(line => !line.includes('interactionLedger.js'));
    } catch (error) {
      return null;
    }
  }

  /**
   * Get all logs
   */
  getAll() {
    return this.logs;
  }

  /**
   * Get logs filtered by type
   */
  getByType(type) {
    return this.logs.filter(log => log.type === type);
  }

  /**
   * Get logs filtered by category
   */
  getByCategory(category) {
    return this.logs.filter(log => log.category === category);
  }

  /**
   * Get recent logs (last N entries)
   */
  getRecent(count = 50) {
    return this.logs.slice(-count);
  }

  /**
   * Get logs in a time range
   */
  getByTimeRange(startTime, endTime) {
    return this.logs.filter(log => {
      return log.time >= startTime && log.time <= endTime;
    });
  }

  /**
   * Search logs by description or data
   */
  search(query) {
    const lowerQuery = query.toLowerCase();
    return this.logs.filter(log => {
      return (
        log.description.toLowerCase().includes(lowerQuery) ||
        JSON.stringify(log.data).toLowerCase().includes(lowerQuery) ||
        log.type.toLowerCase().includes(lowerQuery) ||
        log.category.toLowerCase().includes(lowerQuery)
      );
    });
  }

  /**
   * Clear all logs
   */
  clear() {
    this.logs = [];
    console.log('[Ledger] Cleared all logs');
  }

  /**
   * Enable/disable logging
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
    console.log(`[Ledger] ${enabled ? 'Enabled' : 'Disabled'}`);
  }

  /**
   * Set filters
   */
  setFilters(filters) {
    this.filters = { ...this.filters, ...filters };
  }

  /**
   * Get summary statistics
   */
  getStats() {
    const stats = {
      total: this.logs.length,
      byType: {},
      byCategory: {},
      recentActivity: this.getRecent(10),
    };

    this.logs.forEach(log => {
      stats.byType[log.type] = (stats.byType[log.type] || 0) + 1;
      stats.byCategory[log.category] = (stats.byCategory[log.category] || 0) + 1;
    });

    return stats;
  }

  /**
   * Export logs as JSON
   */
  export() {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Print formatted summary to console
   */
  printSummary() {
    const stats = this.getStats();
    console.group('📊 Interaction Ledger Summary');
    console.log(`Total entries: ${stats.total}`);
    console.log('By Type:', stats.byType);
    console.log('By Category:', stats.byCategory);
    console.log('Recent Activity:', stats.recentActivity);
    console.groupEnd();
  }

  /**
   * Watch for specific patterns and log when they occur
   */
  watch(pattern, callback) {
    // Simple watch implementation - could be enhanced
    const originalLog = this.log.bind(this);
    this.log = function(type, category, data, description) {
      originalLog(type, category, data, description);
      if (pattern(type, category, data, description)) {
        callback({ type, category, data, description });
      }
    };
  }
}

// Create singleton instance
const interactionLedger = new InteractionLedger();

// Make it available globally for console access
if (typeof window !== 'undefined') {
  window.interactionLedger = interactionLedger;
  
  // Add helpful console shortcuts
  window.ledger = {
    // Quick access
    all: () => interactionLedger.getAll(),
    recent: (count) => interactionLedger.getRecent(count),
    clear: () => interactionLedger.clear(),
    stats: () => interactionLedger.getStats(),
    summary: () => interactionLedger.printSummary(),
    
    // Filtering
    byType: (type) => interactionLedger.getByType(type),
    byCategory: (category) => interactionLedger.getByCategory(category),
    search: (query) => interactionLedger.search(query),
    
    // Time-based
    last: (seconds) => {
      const now = Date.now();
      return interactionLedger.getByTimeRange(now - (seconds * 1000), now);
    },
    
    // Export
    export: () => interactionLedger.export(),
    
    // Control
    enable: () => interactionLedger.setEnabled(true),
    disable: () => interactionLedger.setEnabled(false),
  };
  
  // Set up dry.ledger() function to open User Logs modal
  // This will be called from components that have access to modalState
  window.DRY = window.DRY || {};
  window.DRY._openUserLogs = null; // Will be set by a component with modalState access
  
  window.DRY.ledger = () => {
    if (window.DRY._openUserLogs) {
      window.DRY._openUserLogs();
    } else {
      console.warn('User Logs modal not available yet. Please try again in a moment.');
      console.log('You can still use window.ledger for console access:', window.ledger);
    }
  };
  
  console.log('%c🔍 Interaction Ledger loaded!', 'color: #4CAF50; font-weight: bold;');
  console.log('%cUse window.ledger or window.interactionLedger in the console', 'color: #2196F3;');
  console.log('%cTry: ledger.summary(), ledger.recent(20), ledger.byType("click")', 'color: #FF9800;');
  console.log('%cOr use: dry.ledger() to open the User Logs UI', 'color: #9C27B0;');
}

export default interactionLedger;

